import { useEffect, useRef } from "react"

import { useLocation, useSearch } from "wouter"

import { DATE_SEARCH_PARAM_NAME } from "@/config"
import { formatDateForUrl } from "@/utils/dateUtils"
import { DEFAULT_RESORT_SLUG } from "@/utils/resortSlugMap"

import { parseDateParam } from "../../schemas/urlParamSchemas"
import useDateParam from "../useDateParam"
import useResortParam from "../useResortParam"

/**
 * Seeds URL with default values for params that are missing or invalid.
 * Runs once on mount and ensures the URL always has valid params.
 * Internal hook - not exported from useSkipassFlow.
 */
const useSeedUrlDefaults = () => {
  const search = useSearch()
  const [, navigate] = useLocation()
  const { resortSlug, isValidResort } = useResortParam()
  const { startDate } = useDateParam()

  const hasSeededDefaults = useRef(false)

  useEffect(() => {
    if (hasSeededDefaults.current) return
    hasSeededDefaults.current = true

    const searchParams = new URLSearchParams(search)
    let needsDateSeed = false

    const parsed = parseDateParam(searchParams.get(DATE_SEARCH_PARAM_NAME))
    if (!parsed || formatDateForUrl(parsed) < formatDateForUrl(new Date())) {
      searchParams.set(DATE_SEARCH_PARAM_NAME, formatDateForUrl(startDate))
      needsDateSeed = true
    }

    if (!isValidResort || needsDateSeed) {
      const finalSlug = isValidResort ? resortSlug : DEFAULT_RESORT_SLUG
      const query = searchParams.toString()
      navigate(`/${finalSlug}/${query ? `?${query}` : ""}`, { replace: true })
    }
  }, [search, navigate, resortSlug, isValidResort, startDate])
}

export default useSeedUrlDefaults
